import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

interface PeriodCtx {
  /** Primeiro dia do mês selecionado no cabeçalho. */
  period: Date;
  /** "YYYY-MM-DD" do primeiro e do último dia do mês selecionado. */
  from: string;
  to: string;
  /** Chave estável (YYYY-MM) para dependências de efeitos. */
  key: string;
  shift: (n: number) => void;
  /** Vai direto para o mês de uma data "YYYY-MM-DD". */
  goTo: (isoDate: string) => void;
}

const Ctx = createContext<PeriodCtx | null>(null);

/** Mês selecionado no seletor do cabeçalho; as telas de Início, Contas e Extratos e Contas a Pagar e Receber o respeitam. */
export function PeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriod] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const shift = useCallback((n: number) => setPeriod((d) => new Date(d.getFullYear(), d.getMonth() + n, 1)), []);
  const goTo = useCallback((d: string) => setPeriod(new Date(+d.slice(0, 4), +d.slice(5, 7) - 1, 1)), []);
  const value = useMemo<PeriodCtx>(() => ({
    period, shift, goTo,
    from: iso(period),
    to: iso(new Date(period.getFullYear(), period.getMonth() + 1, 0)),
    key: iso(period).slice(0, 7),
  }), [period, shift, goTo]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePeriod(): PeriodCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePeriod fora do PeriodProvider");
  return v;
}
